import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronRight, HelpCircle, Layers, Pencil } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useSelector } from 'react-redux'
import api from '../../../const/axios'

interface AuthorProps {
  author: {
    id: number
    fullname: string
    questions: number
    themes: number
    editors?: number
    user?: { id: number } | null
  }
}

interface StatChipProps {
  count: number
  tooltip: string
  icon: React.ReactNode
  to?: string
}

const StatChip: React.FC<StatChipProps> = ({ count, tooltip, icon, to }) => {
  const cls = cn(
    'flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border transition-colors',
    count > 0 && to
      ? 'bg-gray-50 dark:bg-gray-950 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900'
      : 'bg-gray-50 dark:bg-gray-950 text-gray-300 border-gray-100 dark:border-gray-800 cursor-default'
  )
  const inner = <>{icon}<span>{count}</span></>

  const chip = count > 0 && to
    ? <Link to={to} className={cls}>{inner}</Link>
    : <span className={cls}>{inner}</span>

  return (
    <Tooltip>
      <TooltipTrigger asChild>{chip}</TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  )
}

const Author: React.FC<AuthorProps> = ({ author }) => {
  const { t } = useTranslation()
  const userState = useSelector((state: any) => state.user)
  const isLoggedIn = userState?.isLoggedIn ?? false
  const [claimLoading, setClaimLoading] = React.useState(false)
  const [claimed, setClaimed] = React.useState(false)

  const initials = author.fullname
    .split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()

  const submitClaim = async () => {
    if (!isLoggedIn || claimLoading) return
    setClaimLoading(true)
    try {
      const res = await api.post(`authors/${author.id}/claim`)
      alert(res?.data?.message || 'Müraciət göndərildi')
      setClaimed(true)
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Müraciət göndərilə bilmədi')
    } finally {
      setClaimLoading(false)
    }
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 p-4 flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 text-xs font-bold text-white bg-slate-500">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          {author.user ? (
            <Link
              to={`/profile/${author.user.id}`}
              className="flex items-center gap-1 font-semibold text-gray-900 dark:text-gray-50 hover:text-blue-500 transition-colors group truncate dark:hover:text-indigo-200"
            >
              <span className="truncate">{author.fullname}</span>
              <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
            </Link>
          ) : (
            <div className="space-y-1">
              <p className="font-semibold text-gray-900 dark:text-gray-50 truncate">{author.fullname}</p>
              {/* {isLoggedIn ? (
                <button
                  type="button"
                  disabled={claimLoading || claimed}
                  onClick={submitClaim}
                  className="text-[11px] font-semibold text-blue-600 hover:text-blue-700 disabled:opacity-50 dark:text-indigo-300 dark:hover:text-indigo-200"
                >
                  {claimed ? 'Müraciət göndərildi' : claimLoading ? 'Göndərilir...' : 'Bu müəllifi claim et'}
                </button>
              ) : (
                <span className="text-[11px] text-gray-400 dark:text-gray-500">Claim üçün daxil olun</span>
              )} */}
            </div>
          )}
        </div>
      </div>

      <TooltipProvider delayDuration={300}>
        <div className="flex flex-wrap gap-1.5">
          <StatChip count={author.editors ?? 0} tooltip={t('common.edit')}      icon={<Pencil className="w-3 h-3" />}     to={`/authors/${author.id}/edits`} />
          <StatChip count={author.questions}    tooltip={t('packages.question')} icon={<HelpCircle className="w-3 h-3" />} to={`/authors/${author.id}/questions`} />
          <StatChip count={author.themes}       tooltip={t('authors.themes')}    icon={<Layers className="w-3 h-3" />}     to={`/authors/${author.id}/themes`} />
        </div>
      </TooltipProvider>
    </div>
  )
}

export default Author
